// File: src/ui/HireFormUI.js
// The hire form's building blocks (Oct 2026 redesign). Styles: src/styles/hireform.css.
//
// Every component here is declared at MODULE scope — never inside another
// component. A component declared inside another is a new type on every render,
// so React throws its subtree away and form state goes with it (see CLAUDE.md,
// "Never declare a component inside another component").

import React from 'react';

export const HELP_PHONE_DISPLAY = '01273 911382';
export const HELP_PHONE_TEL = 'tel:01273911382';
export const HELP_EMAIL = 'info@oooshtours.co.uk';
export const TERMS_URL = 'https://www.oooshtours.co.uk/files/Ooosh_vehicle_hire_terms.pdf';
export const LOGO_SRC = '/ooosh-logo.jpg';

// The four parts every driver goes through. Which step belongs to which part
// is decided where each step is rendered (App.js renderStep).
export const PHASE_LABELS = ['You', 'Licence', 'Address', 'Sign'];
export const PHASE_TITLES = ['About you', 'Your licence', 'Your address', 'Final checks & sign'];
export const PHASE = { YOU: 0, LICENCE: 1, ADDRESS: 2, SIGN: 3 };

export const Logo = ({ large = false }) => (
  <img src={LOGO_SRC} alt="Ooosh! Tours" className={`hf-logo${large ? ' hf-logo-lg' : ''}`} />
);

export const PhaseBar = ({ phase }) => (
  <div>
    <div className="hf-phases" aria-label={`Part ${phase + 1} of 4`}>
      {PHASE_LABELS.map((label, i) => {
        const state = i < phase ? 'is-done' : i === phase ? 'is-current' : 'is-todo';
        return (
          <div key={label} className={`hf-phase ${state}`}>
            <div className="hf-phase-bar" />
            <div className="hf-phase-label">{i < phase ? `✓ ${label}` : label}</div>
          </div>
        );
      })}
    </div>
    <p className="hf-phase-count">Part {phase + 1} of 4</p>
  </div>
);

export const Footer = ({ showSaved = false }) => (
  <footer className="hf-footer">
    {showSaved && (
      <span className="hf-saved"><span className="hf-saved-dot" aria-hidden="true" />Saved as you go</span>
    )}
    <span>
      Stuck? Call the Ooosh team on <a href={HELP_PHONE_TEL}>{HELP_PHONE_DISPLAY}</a>
    </span>
  </footer>
);

/** Page frame for a step that belongs to one of the four parts. */
export const StepShell = ({ jobId, phase, showSaved = true, children }) => (
  <div className="hf-page">
    <div className="hf-column">
      <div className="hf-header">
        <Logo />
        <div className="hf-header-text">
          <p className="hf-kicker">{jobId ? `Job #${jobId} · ` : ''}Driver hire form</p>
          <p className="hf-step-title">{PHASE_TITLES[phase]}</p>
        </div>
        <a className="hf-help" href={HELP_PHONE_TEL}>Help</a>
      </div>
      <PhaseBar phase={phase} />
      <div className="hf-shell-body hf-column" style={{ padding: 0 }}>
        {children}
      </div>
      <Footer showSaved={showSaved} />
    </div>
  </div>
);

/** Page frame without the progress bar: start, finished, welcome back, errors. */
export const PlainShell = ({ children, showSaved = false }) => (
  <div className="hf-page">
    <div className="hf-column">
      <div className="hf-shell-body hf-column" style={{ padding: 0 }}>
        {children}
      </div>
      <Footer showSaved={showSaved} />
    </div>
  </div>
);

export const Card = ({ children, className = '' }) => (
  <section className={`hf-card ${className}`}>{children}</section>
);

/** Full-width primary button. While disabled, the label says what's missing. */
export const PrimaryButton = ({ children, busy = false, busyLabel, disabled, ...rest }) => (
  <button type="button" className="hf-btn" disabled={disabled || busy} {...rest}>
    {busy && <span className="hf-spinner hf-spinner-sm" aria-hidden="true" />}
    <span>{busy && busyLabel ? busyLabel : children}</span>
  </button>
);

/** Yes / No, both styled the same. No comes first. Values are 'yes' / 'no' / null. */
export const YesNo = ({ value, onChange, name }) => (
  <div className="hf-segment" role="radiogroup" aria-label={name}>
    {[['no', 'No'], ['yes', 'Yes']].map(([v, label]) => (
      <button
        key={v}
        type="button"
        role="radio"
        aria-checked={value === v}
        className={value === v ? 'is-selected' : ''}
        onClick={() => onChange(v)}
      >
        {label}
      </button>
    ))}
  </div>
);

export const Note = ({ tone = 'amber', children }) => (
  <p className={`hf-note hf-note-${tone}`}>{children}</p>
);

/** "Why we ask" / "What happens next" line at the foot of a card. */
export const Why = ({ lead, children }) => (
  <p className="hf-why"><strong>{lead}</strong> {children}</p>
);

export const Spinner = () => <div className="hf-spinner" role="status" aria-label="Loading" />;

/** A tickable declaration. The whole row is the tap target. */
export const CheckRow = ({ checked, onToggle, children }) => (
  <button type="button" className="hf-check-row" role="checkbox" aria-checked={checked} onClick={onToggle}>
    <span className={`hf-checkbox${checked ? ' is-on' : ''}`} aria-hidden="true">{checked ? '✓' : ''}</span>
    <span>{children}</span>
  </button>
);

/** Numbered / ticked list row (start page parts, welcome-back progress). */
export const StepRow = ({ marker, markerState = '', title, desc, aside, asideState = '' }) => (
  <div className="hf-step-row">
    <span className={`hf-dot ${markerState}`}>{marker}</span>
    <div className="hf-step-row-main">
      <p className="hf-step-row-title">{title}</p>
      {desc && <p className="hf-step-row-desc">{desc}</p>}
    </div>
    {aside && <span className={`hf-step-row-aside ${asideState}`}>{aside}</span>}
  </div>
);

export const SummaryRow = ({ label, value, strong = false }) => (
  <div className={`hf-summary-row${strong ? ' is-strong' : ''}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

/** First name for a greeting, from OP's full name ("MR JO BLOGGS" → "Jo"). */
export function firstNameOf(fullName) {
  if (!fullName) return null;
  const first = String(fullName).trim().replace(/^(MR|MRS|MS|MISS|MX|DR|PROF)\.?\s+/i, '').split(/\s+/)[0];
  if (!first) return null;
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

/** 1 → "1st", 22 → "22nd", 13 → "13th". */
export function ordinal(n) {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
}

/**
 * Hire dates in words (jon, Oct 2026): "22nd to 24th Oct", "30th Oct to 2nd Nov",
 * one day: "2nd Jan". Takes anything Date can parse; null if the start isn't a date.
 */
export function formatHireRange(start, end) {
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  const ok = (d) => d && !Number.isNaN(d.getTime());
  if (!ok(s)) return null;
  const month = (d) => d.toLocaleDateString('en-GB', { month: 'short' });
  const day = (d) => ordinal(d.getDate());
  if (!ok(e) || s.toDateString() === e.toDateString()) return `${day(s)} ${month(s)}`;
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${day(s)} to ${day(e)} ${month(s)}`;
  }
  return `${day(s)} ${month(s)} to ${day(e)} ${month(e)}`;
}
